import React, { Suspense, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Backdrop,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Download, FileQuestion, FileWarning } from "lucide-react";

import UIButton from "../UIButton";
import UIModal from "../UIModal";
import { FILE_PREVIEW_KIND, resolveFilePreviewKind } from "./filePreviewKind";

import "./FilePreviewModal.scss";

const PdfPreview = React.lazy(() => import("./PdfPreview"));
const SheetPreview = React.lazy(() => import("./SheetPreview"));

/** Stops a failed lazy chunk load / render throw from bubbling to the app. */
class PreviewBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

PreviewBoundary.propTypes = { onError: PropTypes.func, children: PropTypes.node };

/**
 * Presentational file-preview dialog. The caller fetches the file and passes a
 * ready `fileUrl` (https or blob:); an empty `fileUrl` reads as still loading and
 * `failed` as "couldn't fetch". Images, PDFs, spreadsheets and (self-contained)
 * HTML render inline; every other type shows the unsupported note. Download and
 * Close stay available.
 */
const FilePreviewModal = ({
  open,
  fileName,
  contentType,
  meta,
  fileUrl,
  failed,
  isRtl,
  labels,
  downloadDisabled,
  onDownload,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [renderFailed, setRenderFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [htmlDoc, setHtmlDoc] = useState("");
  const handleRenderFailed = useCallback(() => setRenderFailed(true), []);
  const handleReady = useCallback(() => setReady(true), []);

  // A new file (or a different type) gets a fresh render attempt.
  useEffect(() => {
    setRenderFailed(false);
    setReady(false);
    setHtmlDoc("");
  }, [fileUrl, contentType]);

  const kind = resolveFilePreviewKind(contentType, fileName);

  // HTML renders through `srcDoc`, not `src` — iOS Safari leaves a `blob:`-src
  // iframe blank. The blob URL is local, so this read is instant.
  useEffect(() => {
    if (kind !== FILE_PREVIEW_KIND.html || !fileUrl) return undefined;
    let cancelled = false;
    fetch(fileUrl)
      .then((response) => response.text())
      .then((text) => {
        if (!cancelled) setHtmlDoc(text || " ");
      })
      .catch(() => {
        if (!cancelled) handleRenderFailed();
      });
    return () => {
      cancelled = true;
    };
  }, [kind, fileUrl, handleRenderFailed]);

  let body = null;
  if (!open) {
    body = null; // nothing to compute while the dialog fades out
  } else if (failed || renderFailed) {
    body = (
      <div className="file-preview-modal__state">
        <FileWarning size={40} strokeWidth={1.5} aria-hidden />
        <p className="file-preview-modal__state-text">{labels.loadError}</p>
      </div>
    );
  } else if (kind === FILE_PREVIEW_KIND.unsupported) {
    body = (
      <div className="file-preview-modal__state">
        <FileQuestion size={40} strokeWidth={1.5} aria-hidden />
        <p className="file-preview-modal__state-text">{labels.unsupported}</p>
      </div>
    );
  } else {
    let content = null;
    if (kind === FILE_PREVIEW_KIND.image) {
      content = (
        <div className="file-preview-modal__image-wrap">
          <img
            className="file-preview-modal__image"
            src={fileUrl}
            alt={fileName}
            onLoad={handleReady}
            onError={handleRenderFailed}
          />
        </div>
      );
    } else if (kind === FILE_PREVIEW_KIND.html) {
      // `allow-scripts` lets JS-driven reports render, but WITHOUT
      // `allow-same-origin` the frame stays a null origin: no access to our
      // cookies / localStorage / API session, no reading `window.parent`.
      // `allow-popups`/`allow-top-navigation` stay off. Never add
      // `allow-same-origin` alongside `allow-scripts` (the frame could then
      // strip its own sandbox).
      content = htmlDoc && (
        <iframe
          key={fileUrl}
          className="file-preview-modal__html"
          srcDoc={htmlDoc}
          title={fileName}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          onLoad={handleReady}
        />
      );
    } else if (kind === FILE_PREVIEW_KIND.spreadsheet) {
      content = (
        // `key` remounts the boundary per file so a past failure can't stick.
        <PreviewBoundary key={fileUrl} onError={handleRenderFailed}>
          <Suspense fallback={null}>
            <SheetPreview
              file={fileUrl}
              labels={labels}
              onReady={handleReady}
              onError={handleRenderFailed}
            />
          </Suspense>
        </PreviewBoundary>
      );
    } else {
      content = (
        <PreviewBoundary key={fileUrl} onError={handleRenderFailed}>
          <Suspense fallback={null}>
            <PdfPreview
              file={fileUrl}
              labels={labels}
              isRtl={isRtl}
              onReady={handleReady}
              onError={handleRenderFailed}
            />
          </Suspense>
        </PreviewBoundary>
      );
    }

    // One loader — same look as the app's global loader — from open, through the
    // fetch, until the file actually paints.
    body = (
      <div className="file-preview-modal__loadable">
        {fileUrl ? content : null}
        <Backdrop
          open={!ready}
          sx={{ position: "absolute", color: "#fff", zIndex: 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }

  const actions = (
    <div className="ui-modal__actions">
      <UIButton type="button" variant="outline" size="medium" onClick={onClose}>
        {labels.close}
      </UIButton>
      {onDownload && (
        <UIButton
          type="button"
          variant="primary"
          size="medium"
          startIcon={<Download size={16} aria-hidden />}
          disabled={downloadDisabled}
          onClick={onDownload}
        >
          {labels.download}
        </UIButton>
      )}
    </div>
  );

  return (
    <UIModal
      open={open}
      type="info"
      title={<bdi>{fileName}</bdi>}
      description={meta || undefined}
      actions={actions}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ className: "file-preview-modal" }}
    >
      <div className="file-preview-modal__body">{body}</div>
    </UIModal>
  );
};

FilePreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  fileName: PropTypes.string,
  contentType: PropTypes.string,
  meta: PropTypes.node,
  fileUrl: PropTypes.string,
  failed: PropTypes.bool,
  isRtl: PropTypes.bool,
  labels: PropTypes.shape({
    download: PropTypes.node,
    close: PropTypes.node.isRequired,
    unsupported: PropTypes.node,
    loadError: PropTypes.node,
    pageOf: PropTypes.string,
    previousPage: PropTypes.string,
    nextPage: PropTypes.string,
    sheetTruncated: PropTypes.string,
  }).isRequired,
  downloadDisabled: PropTypes.bool,
  /** Omit to hide the Download button (e.g. previewing a file the user just picked). */
  onDownload: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

FilePreviewModal.defaultProps = {
  fileName: "",
  contentType: "",
  meta: null,
  fileUrl: "",
  failed: false,
  isRtl: false,
  downloadDisabled: false,
  onDownload: undefined,
};

export default FilePreviewModal;
